// src/pages/ParentRegister.js (v5 – config-driven, clean)

import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";

import api from "../services/api";
import { API_PATHS, ROUTES } from "../config";
import "../styles/ParentRegister.css";

/**
 * Parent registration page.
 *
 * Handles invite code from URL, suggests display name from email,
 * creates account, and routes to gallery on success.
 *
 * @component
 * @returns {JSX.Element}
 */
const ParentRegister = () => {
  const navigate = useNavigate();

  const [email, setEmail] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [displayNameDirty, setDisplayNameDirty] = useState(false);
  const [password, setPassword] = useState("");
  const [inviteCode, setInviteCode] = useState("");
  const [inviteCodeLocked, setInviteCodeLocked] = useState(false);

  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // Extract invite code from URL on mount
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const code = params.get("code");
    if (code) {
      setInviteCode(code);
      setInviteCodeLocked(true);
    }
  }, []);

  // Auto-suggest display name from email (only if user hasn't typed)
  useEffect(() => {
    if (!displayNameDirty && email) {
      const local = email.split("@")[0];
      const suggested = local
        .replace(/[._-]+/g, " ")
        .split(" ")
        .filter(Boolean)
        .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
        .join(" ");
      setDisplayName(suggested);
    }
  }, [email, displayNameDirty]);

  const handleRegister = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");
    setSubmitting(true);

    const finalDisplayName = displayName.trim() || email.split("@")[0] || "";

    const payload = {
      email: email.trim(),
      password,
      display_name: finalDisplayName,
      invite_code: inviteCode,
    };

    try {
      await api.post(API_PATHS.PARENT_REGISTER, payload);

      localStorage.removeItem("token");
      localStorage.removeItem("refreshToken");
      localStorage.removeItem("role");
      localStorage.removeItem("schoolName");
      window.dispatchEvent(new Event("gg-auth-changed"));

      setSuccess("Account created! Continue on your phone to add your profile photo.");

      setTimeout(() => {
        navigate(ROUTES.CONTINUE_IN_APP, {
          state: {
            email: email.trim(),
            role: "PARENT",
          },
        });
      }, 1200);
    } catch (err) {
      const detail = err?.response?.data?.detail;

      let friendlyMessage = detail || "Something went wrong. Please try again.";

      if (detail === "Invalid or expired invite code") {
        friendlyMessage =
          "This invite link has expired or is no longer valid. " +
          "Please ask your child's school for a new Parent Invite link.";
      } else if (detail === "Email already registered") {
        friendlyMessage =
          "This email is already registered. Please use the Login page or reset your password.";
      }

      setError(friendlyMessage);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="parent-register-container page">
      <div className="parent-register-card">
        <header className="parent-register-header">
          <h1 className="parent-register-title">Create your Parent Account</h1>
          <p className="parent-register-subtitle">
            Use the invite link from your child&apos;s school to set up secure access to their class photos.
          </p>
        </header>

        {error && <p className="error-text" aria-live="polite">{error}</p>}
        {success && <p className="success-text" aria-live="polite">{success}</p>}

        <form onSubmit={handleRegister} className="parent-register-form">
          <div className="parent-field-group">
            <label htmlFor="parent-email" className="parent-label">
              Your Email
            </label>
            <input
              id="parent-email"
              type="email"
              className="parent-input"
              placeholder="you@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              disabled={submitting}
            />
          </div>

          <div className="parent-field-group">
            <label htmlFor="parent-display-name" className="parent-label">
              Display Name
            </label>
            <input
              id="parent-display-name"
              type="text"
              className="parent-input"
              placeholder="How other parents will see you"
              value={displayName}
              onChange={(e) => {
                setDisplayName(e.target.value);
                setDisplayNameDirty(true);
              }}
              required
              disabled={submitting}
            />
            <small className="hint-text">
              You can change this later from your Profile.
            </small>
          </div>

          <div className="parent-field-group">
            <label htmlFor="parent-password" className="parent-label">
              Create Password
            </label>
            <input
              id="parent-password"
              type="password"
              className="parent-input"
              placeholder="Choose a secure password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              disabled={submitting}
            />
          </div>

          <div className="parent-field-group">
            <label htmlFor="parent-invite-code" className="parent-label">
              Invite Code
            </label>
            <input
              id="parent-invite-code"
              type="text"
              className="parent-input"
              placeholder="From your school's invite link"
              value={inviteCode}
              onChange={(e) => setInviteCode(e.target.value)}
              disabled={inviteCodeLocked || submitting}
              required
            />
            {inviteCodeLocked && (
              <small className="hint-text">
                This code was taken from your invite link. If it looks wrong, ask your school for a new one.
              </small>
            )}
          </div>

          <button
            type="submit"
            className="parent-register-button"
            disabled={submitting}
          >
            {submitting ? "Creating account…" : "Register"}
          </button>
        </form>
      </div>
    </div>
  );
};

export default ParentRegister;